import config from '@/config';
import { TeachersInviteLinkResponse } from '@/school-management/types/response';
import {
  AdminManagementService,
  SchoolDetailsResponse,
  SchoolDetailsError,
} from '@/school-management/types/services/adminManagement';
import {
  UserTimeAndRegistrationStatsResponse,
  UserTimeAndRegistrationDataPayload,
  UserStatisticsResponse,
  UserStatisticsRequest,
} from '@/school-management/types/response';
import axiosInstance from '@/school-management/utils/api';
import { AxiosResponse } from 'axios';
import { UpdateSchoolNameResponse } from '@/school-management/types/response';
import { AxiosError } from 'axios';
import { UserData } from '@/school-management/types/user-data';
import { UserState } from '@/types/user';

const createSchoolDetailsError = (error: any, schoolId: string): SchoolDetailsError => {
  const axiosError = error as AxiosError;
  const status = axiosError.response?.status || 500;
  const responseData = axiosError.response?.data as any;

  // Extract error details from response
  const message = responseData?.message || axiosError.message || 'An unexpected error occurred';
  const code = responseData?.code || `HTTP_${status}`;

  const schoolError: SchoolDetailsError = {
    code,
    message,
    statusCode: status,
    schoolId,
    details: responseData?.details || responseData,
  };

  // Log error for debugging
  console.error(`[AdminManagement] School details error for ID ${schoolId}:`, {
    code,
    message,
    status,
    url: axiosError.config?.url,
    details: schoolError.details,
  });

  return schoolError;
};

const schoolDetailsInFlight = new Map<string, Promise<SchoolDetailsResponse | null>>();

export const adminManagementService: AdminManagementService = {
  getSchoolDetails: async (schoolId: string): Promise<SchoolDetailsResponse | null> => {
    try {
      if (!schoolId || schoolId.trim() === '') {
        console.error('School ID is required and cannot be empty');
        return null;
      }

      const existing = schoolDetailsInFlight.get(schoolId);
      if (existing) return existing;

      const promise = (async () => {
        try {
          const url = `/admin-management/${config.apiVersion}/organization/${schoolId}`;
          const resp: SchoolDetailsResponse | null = await axiosInstance.get(url);
          return resp;
        } catch (error: any) {
          console.error('Error fetching school details:', error);
          const schoolError = createSchoolDetailsError(error, schoolId);
          console.error('[AdminManagement] Detailed error:', schoolError);
          return null;
        } finally {
          schoolDetailsInFlight.delete(schoolId);
        }
      })();

      schoolDetailsInFlight.set(schoolId, promise);
      return promise;
    } catch {
      return null;
    }
  },
  updateOrganizationName: async (
    organizationId: string,
    organizationName: string
  ): Promise<AxiosResponse<UpdateSchoolNameResponse>> => {
    const url = `/admin-management/${config.apiVersion}/organization/${organizationId}`;
    return axiosInstance.post(url, { organizationName });
  },
  getAdminGuideLink: async (): Promise<string> => {
    const url = `/admin-management/${config.apiVersion}/organization/guides`;
    let adminGuideLink = '';
    let error;

    try {
      adminGuideLink = (await axiosInstance.get(url))?.data?.data;
    } catch (er: any) {
      error = er;
    }

    if (!adminGuideLink) {
      console.error('Error fetching admin Guide Link:', error);
    }

    return adminGuideLink;
  },
  getTeachersInviteLink: async (
    schoolId: string
  ): Promise<AxiosResponse<TeachersInviteLinkResponse> | null> => {
    try {
      if (!schoolId || schoolId.trim() === '') {
        console.error('School ID is required and cannot be empty');
        return null;
      }

      const url = `/admin-management/${config.apiVersion}/${schoolId}/teachers/invitation-link`;
      const resp: AxiosResponse<TeachersInviteLinkResponse> | null = await axiosInstance.get(url);

      return resp;
    } catch (error: any) {
      console.error('Error fetching school details:', error);
      return null;
    }
  },
  createCsvTeachers: async (
    user: UserState | null,
    groupId: string | undefined,
    teachers: UserData[]
  ) => {
    const url = `/admin-management/${config.apiVersion}/${groupId}/teachers`;
    return axiosInstance.post(url, {
      organizationId: user?.metadata.organizationDetails.organizationId,
      groupId,
      teachers,
    });
  },
  getUsersTimeAndRegistrationData: async (
    payload: UserTimeAndRegistrationDataPayload
  ): Promise<AxiosResponse<UserTimeAndRegistrationStatsResponse> | null> => {
    try {
      const url = `/admin-management/${config.apiVersion}/get-users-time-and-registration-data`;
      const response: AxiosResponse<UserTimeAndRegistrationStatsResponse> =
        await axiosInstance.post(url, payload);
      return response;
    } catch (error: any) {
      console.error('Error fetching users time and registration data:', error);
      return null;
    }
  },
  getUserStatistics: async (
    payload?: UserStatisticsRequest
  ): Promise<AxiosResponse<UserStatisticsResponse> | null> => {
    try {
      const url = `/admin-management/${config.apiVersion}/metrics/user-statistics`;
      const response: AxiosResponse<UserStatisticsResponse> = await axiosInstance.post(url, {
        ...(payload ?? {}),
        filterByCurrentLevel: !!payload?.filterByCurrentLevel,
      });
      return response;
    } catch (error: any) {
      console.error('Error fetching user statistics:', error);
      return null;
    }
  },
};
